import { useEffect, useState } from "react";
import { Loader2, Plus, Radar, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { errMsg } from "../ui";

const SEL = "h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm";

function PhoneSuiteRow({ e, i, aliases, onChange, onRemove }) {
  const n = Object.keys(e.territory_map || {}).length;
  return (
    <tr className="border-t border-slate-100" data-testid={`bsp-ps-row-${i}`}>
      <td className="py-1.5 pr-2"><Input value={e.territory} onChange={(ev) => onChange({ ...e, territory: ev.target.value })} className="h-8 font-mono text-xs" data-testid={`bsp-ps-territory-${i}`} /></td>
      <td className="px-2"><select value={e.alias} onChange={(ev) => onChange({ ...e, alias: ev.target.value })} className={`${SEL} h-8 text-xs`} data-testid={`bsp-ps-alias-${i}`}>
        <option value="">Pick an API connection…</option>{aliases.map((a) => <option key={a.alias} value={a.alias}>{a.alias}</option>)}</select></td>
      <td className="px-2 text-[11px] text-slate-500">{n ? `${n} domain override${n > 1 ? "s" : ""}` : "—"}</td>
      <td className="pl-2 text-right"><button type="button" onClick={onRemove} className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600" data-testid={`bsp-ps-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button></td>
    </tr>
  );
}

/** Billing Sync → Settings: where the Tracked Items Preview reads Stax Bill and PhoneSuite from. */
export function PreviewSourcesCard({ connections }) {
  const [src, setSrc] = useState(null);
  const [form, setForm] = useState({ mirror_connection_id: "", phonesuite: [] });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/billing-sync/preview/sources").then(({ data }) => {
      setSrc(data); setForm({ mirror_connection_id: data.mirror?.id || "", phonesuite: (data.phonesuite || []).map(({ connection, ...e }) => e) });
    }).catch((e) => toast.error(errMsg(e, "Could not load the preview sources")));
  }, []);
  if (!src) return null;
  const aliases = src.api_connections.filter((a) => a.alias.startsWith("phonesuite_"));
  const setRow = (i, v) => setForm({ ...form, phonesuite: form.phonesuite.map((x, j) => (j === i ? v : x)) });
  const save = async () => {
    setBusy(true);
    try { await api.put("/billing-sync/preview/sources", form); toast.success("Preview sources saved"); }
    catch (e) { toast.error(errMsg(e, "Could not save")); } finally { setBusy(false); }
  };
  const valid = form.mirror_connection_id && form.phonesuite.every((e) => e.territory.trim() && e.alias);
  return (
    <Card className="overflow-hidden border-slate-200 shadow-sm" data-testid="bsp-sources-card">
      <div className="flex items-start gap-3 border-b border-slate-100 px-5 py-4">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-50 text-sky-600"><Radar className="h-4 w-4" /></div>
        <div><h3 className="font-heading text-sm font-semibold text-slate-800">Tracked Items Preview sources</h3>
          <p className="text-xs text-slate-500">Stax Bill tracked items come from the FusebillSync mirror; hotels from each territory's PhoneSuite (Connectware) API. Main PBX and SAS are read through the API connections {Object.entries(src.platform).map(([p, c]) => <span key={p} className="font-mono">{c ? c.alias : `(missing ${p})`} </span>)}.</p></div>
      </div>
      <div className="space-y-4 px-5 py-4">
        <label className="block space-y-1"><span className="text-xs font-medium text-slate-600">FusebillSync mirror</span>
          <select value={form.mirror_connection_id} onChange={(e) => setForm({ ...form, mirror_connection_id: e.target.value })} className={SEL} data-testid="bsp-sources-mirror">
            <option value="">Pick a MySQL connection…</option>{connections.map((c) => <option key={c.id} value={c.id}>{`${c.name} — ${c.host}/${c.database}`}</option>)}</select></label>
        <div>
          <div className="mb-1 text-xs font-medium text-slate-600">PhoneSuite territories (hospitality)</div>
          <table className="w-full text-sm"><thead><tr className="text-[10px] uppercase tracking-wider text-slate-400"><th className="w-[42%] pb-1 text-left">Territory</th><th className="px-2 pb-1 text-left">API connection</th><th className="px-2 pb-1 text-left">Overrides</th><th /></tr></thead>
            <tbody>{form.phonesuite.map((e, i) => <PhoneSuiteRow key={i} e={e} i={i} aliases={aliases} onChange={(v) => setRow(i, v)} onRemove={() => setForm({ ...form, phonesuite: form.phonesuite.filter((_, j) => j !== i) })} />)}</tbody></table>
          <Button size="sm" variant="ghost" className="mt-1" onClick={() => setForm({ ...form, phonesuite: [...form.phonesuite, { territory: "", alias: "", territory_map: {} }] })} data-testid="bsp-ps-add"><Plus className="h-3.5 w-3.5" /> Add territory</Button>
        </div>
        <Button onClick={save} disabled={busy || !valid} data-testid="bsp-sources-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save sources</Button>
      </div>
    </Card>
  );
}
